import React from 'react';
import { Box, Typography, Chip, Stack, Tooltip } from '@mui/material';
import type { RunEntity, RunUIPermissions } from '@/types/run-stage.types';

interface RunHeaderProps {
  run: RunEntity;
  uiPermissions?: RunUIPermissions;
  isSyncing?: boolean;
  onPause?: () => void;
  onResume?: () => void;
  onAbort?: () => void;
}

function getStatusColor(
  status?: string
): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (status?.toUpperCase()) {
    case 'LIVE':
      return 'primary';
    case 'COMPLETED':
      return 'success';
    case 'PAUSED':
      return 'warning';
    case 'ABORTED':
      return 'error';
    default:
      return 'default';
  }
}

function getVersionLabel(version: RunEntity['version']): string {
  if (!version) return '';
  if (typeof version === 'string') return version;
  const num = version.versionNumber ?? version.name ?? '';
  return num ? `v${num}` : '';
}

export const RunHeader: React.FC<RunHeaderProps> = ({
  run,
  isSyncing = false,
  onPause,
  onResume,
  onAbort,
  uiPermissions,
}) => {
  const versionLabel = getVersionLabel(run.version);
  const statusColor = getStatusColor(run.status);

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {run.name || run._id}
          </Typography>
          {run.uid && (
            <Typography variant="caption" color="text.secondary">
              {run.uid}
              {versionLabel ? ` · ${versionLabel}` : ''}
            </Typography>
          )}
        </Box>

        <Chip
          label={run.status || 'LIVE'}
          color={statusColor}
          size="small"
          sx={{ fontWeight: 500 }}
        />

        {isSyncing && (
          <Typography variant="caption" color="text.secondary">
            Syncing…
          </Typography>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {uiPermissions?.canPause && onPause && (
          <Tooltip title="Pause the run">
            <Chip
              label="Pause"
              color="warning"
              size="small"
              variant="outlined"
              onClick={onPause}
              clickable
            />
          </Tooltip>
        )}
        {uiPermissions?.canResume && onResume && (
          <Tooltip title="Resume the run">
            <Chip
              label="Resume"
              color="primary"
              size="small"
              variant="outlined"
              onClick={onResume}
              clickable
            />
          </Tooltip>
        )}
        {uiPermissions?.canAbort && onAbort && (
          <Tooltip title="Abort the run">
            <Chip
              label="Abort"
              color="error"
              size="small"
              variant="outlined"
              onClick={onAbort}
              clickable
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};
